import { CarePlanSectionContainer } from '@features/carePlans/components/CarePlanSectionContainer'
import Signatures from '@features/carePlans/components/CarePlanSignatures'
import { colors, sizes } from '@lmc/common/constants'
import { CAREPLAN_ADL_EOL_ID, CAREPLAN_ADL_PBS_ID } from '@lmc/common/types'
import { Button, FormCard, Heading, Text } from '@lmc/ui-kit'
import { prepareRichEditorContent } from '@lmc/ui-kit/src/dataEntry/RichEditor/normalizeRichEditorHtmlForStorage'
import ConsentSection from 'components/consent/ConsentSection'
import dayjs from 'dayjs'
import _ from 'lodash'
import { useCallback, useEffect, useRef } from 'react'
import { isBrowser, isTablet } from 'react-device-detect'
import { connect } from 'react-redux'
import { useLocation } from 'react-router-dom'
import styled from 'styled-components'
import Actions from '../../../actions'
import { gaEvent } from '../../../common/services/analytics'
import { getNominative } from '../../../common/utils'
import { hasMeaningfulStoredHtml } from '../../../common/utils/html'
import {
  CarePlanDocuments,
  CarePlanNextReviewField,
  CarePlanSectionContent,
  CarePlanSummaryHeader
} from '../../../components/carePlans'
import {
  FormCardHeading,
  NotesBlock,
  NotesLabel,
  NotesText
} from '../../../components/carePlans/carePlanSharedStyles'
import { Spinner } from '../../../components/generic'
import { ViewStoredHtml } from '../../../components/generic/ViewStoredHtml'
import useFeatureFlags from '../../../hooks/useFeatureFlags'
import Operations from '../../../operations'
import RiskAssessmentItem from '../../../screens/Residents/screens/ResidentRiskAssessmentsScreens/components/RiskAssessmentItem'
import SeenBySection from '../../../screens/Residents/screens/ResidentRiskAssessmentsScreens/components/SeenBySection'
import Selectors from '../../../selectors'
import { useCarePlanPopulated } from '../../../shared/hooks/useCarePlanPopulated'
import { history } from '../../../store'
import { withRouter } from '../../../withRouter'
import CarePlanGoalsListInput from './CarePlanGoalsListInput'
import CarePlanLoadError from './CarePlanLoadError'
import { EndOfLifeSections } from './endOfLife'
import FluidTrackingCard from './FluidTrackingCard'
import LegacySkillNeeds from './needs/LegacySkillNeeds'
import NeedsSection from './needs/NeedsSection'
import { SectionsStack } from './SectionsStack'

const EATING_AND_DRINKING_ADL = '5d3ef8e7610ca2e6713c60f0'

export const CarePlanViewScreen = ({
  features,
  fetchNeedsOptions,
  fetchSkillNeedTemplates,
  router,
  signatureItem,
  carers,
  setLoadedListItem,
  carePlanSignatures,
  residents,
  carePlanNominative,
  needsOptions,
  hasGoals,
  pbsFlagEnabled,
  fluidTrackingFlagEnabled,
  hasAccess,
  documents: allDocuments
}) => {
  const formRef = useRef(null)
  const gaEventTriggered = useRef(false)
  const location = useLocation()
  const referrer = location.state?.referrer

  const flags = useFeatureFlags({
    flagNames: [
      'custom_careplan_section',
      'end_of_life_care_plan',
      'informed_consent',
      'mods_needs_care_plan',
      'care_plan_rich_text'
    ]
  })?.data
  // TODO: Remove custom_careplan_section flag once end_of_life_care_plan is enabled for all homes.
  const customCareplanSectionFlagEnabled =
    flags?.custom_careplan_section?.enabled || false
  const eolFlagEnabled = flags?.end_of_life_care_plan?.enabled || false
  const informedConsentFlagEnabled = flags?.informed_consent?.enabled || false
  const needsCarePlanFlagEnabled = flags?.mods_needs_care_plan?.enabled || false
  const richTextEnabled = flags?.care_plan_rich_text?.enabled || false

  const {
    params: { id, care_plan_id, revision_id }
  } = router

  const chosenId = revision_id || care_plan_id
  // Derive isRevision from URL - if revision_id exists, we're viewing a historical revision
  const isViewingRevision = !!revision_id

  const {
    data: carePlan,
    isLoading,
    error,
    refetch
  } = useCarePlanPopulated({
    carePlanId: chosenId,
    enabled: !!chosenId && !!id
  })

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (carePlan && !gaEventTriggered.current) {
      gaEvent({
        category: 'CarePlans',
        action: 'View',
        label: carePlan.carePlanAdl?.name
      })
      gaEventTriggered.current = true
    }
  }, [carePlan])

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (features?.features?.skillsAndNeeds?.enabled) {
      fetchNeedsOptions?.()
      fetchSkillNeedTemplates?.()
    }
  }, [
    features?.features?.skillsAndNeeds?.enabled,
    fetchNeedsOptions,
    fetchSkillNeedTemplates
  ])

  const handleRiskAssessmentClick = useCallback(
    (riskId) => {
      formRef.current?.scrollIntoView()
      window.scrollTo(0, 0)
      history.push(`/residents/${id}/risk-assessments/${riskId}`)
    },
    [id]
  )

  const onEdit = useCallback(() => {
    gaEvent({
      category: 'CarePlans',
      action: 'Edit',
      label: carePlan?.carePlanAdl?.name
    })
    history.push(
      `/residents/${id}/care-plans/${care_plan_id}/edit`,
      location.state
    )
  }, [id, care_plan_id, carePlan?.carePlanAdl?.name, location])

  if (error && !carePlan) return <CarePlanLoadError onRetry={refetch} />

  if (isLoading || !id || !carePlan || !carers || !residents) {
    return <Spinner />
  }

  const resident = _.find(residents, { id })
  const editedByCarer = carePlan.editedBy
    ? _.find(carers, { id: carePlan.editedBy })
    : null
  const preferredCarePlanName = getNominative(carePlanNominative, 'Care Plan')
  const skillNeeds = carePlan?.skillNeeds
  const isPBSAssessment =
    pbsFlagEnabled && carePlan.carePlanAdl?.id === CAREPLAN_ADL_PBS_ID
  const isEolCarePlan =
    eolFlagEnabled && carePlan.carePlanAdl?.id === CAREPLAN_ADL_EOL_ID
  const hasRolePermission = hasAccess(
    ['carehome-admin', 'senior-carer'],
    [
      {
        i: 'update',
        this: 'CarePlan'
      }
    ]
  )
  const baseUrl = `/residents/${id}/care-plans`
  const logs = carePlan.logs || []
  const populatedDocuments = carePlan.populatedDocuments || []
  const residentDocuments = _.filter(allDocuments ?? [], { resident: id }) ?? []
  const documents = _.uniqBy(
    [...(residentDocuments || []), ...(populatedDocuments || [])],
    'id'
  ).sort((a, b) => (a.id || '').localeCompare(b.id || ''))
  const riskAssessments = carePlan.populatedRiskAssessments || []
  const fluidTrackingEnabled = carePlan?.fluidTracking?.enabled

  const renderHeader = () => (
    <CarePlanSummaryHeader
      carePlan={{ ...carePlan, isRevision: isViewingRevision, riskAssessments }}
      carer={editedByCarer}
      resident={resident}
      logs={logs}
      onClickBack={() => {
        if (isViewingRevision) {
          // When viewing a revision, go back to the history list
          router.navigate(`${baseUrl}/${care_plan_id}/history`)
        } else {
          // When viewing active care plan, go to care plans list
          router.navigate(referrer || baseUrl)
        }
      }}
      onClickHistory={() =>
        router.navigate(`${baseUrl}/${care_plan_id}/history`)
      }
      carePlanNominative={carePlanNominative}
      titleContainerStyle={{ marginBottom: 24 }}
    />
  )

  const renderRisks = () => {
    let Risks = null

    if (riskAssessments && riskAssessments.length) {
      Risks = _.sortBy(riskAssessments, 'form.name').map((row) => {
        const hasActions = !!row?.form?.hasActions && !!row?.formData?.actions

        return (
          <div key={row.id}>
            <RiskAssessmentItem
              carer={row.createdBy}
              showUpdateAlert={dayjs(carePlan.editedOn).isBefore(row.createdOn)}
              riskAssessment={row}
              onView={handleRiskAssessmentClick}
              carePlanNominative={carePlanNominative}
            />
            {hasActions && (
              <WrapperActions>
                <StyledHeading level={3}>
                  Actions for {row?.form?.name}
                </StyledHeading>
                <StyledText>{row?.formData?.actions}</StyledText>
              </WrapperActions>
            )}
          </div>
        )
      })
    }

    const hasRisks = !!Risks?.length

    return (
      <FormCard title="Risks">
        {hasRisks ? <div style={styles.risksSection}>{Risks}</div> : null}
      </FormCard>
    )
  }

  // Render documents
  const renderDocuments = () => (
    <CarePlanDocuments
      carePlan={carePlan}
      documents={documents}
      style={{ paddingBottom: 0 }}
    />
  )

  const renderFluidTracking = () => {
    if (
      carePlan.carePlanAdl?.id !== EATING_AND_DRINKING_ADL ||
      !fluidTrackingFlagEnabled ||
      !fluidTrackingEnabled
    ) {
      return null
    }

    return (
      <FluidTrackingCard
        resident={resident}
        carePlan={carePlan}
        fluidTrackingFlagEnabled={fluidTrackingFlagEnabled}
      />
    )
  }

  const renderNextReview = () => (
    <CarePlanNextReviewField
      date={carePlan.nextReviewDate}
      noFurtherSupportRequired={carePlan.noFurtherSupportRequired}
    />
  )

  const renderSeenBy = () => {
    if (!carePlan.seenBy || !carePlan.seenBy.length) return null

    const filteredAvatars = carePlan.seenBy
      .filter((seenByItem) => seenByItem)
      .map((seenByItem) => {
        const carerId = seenByItem.id || seenByItem
        const carer = _.find(carers, { id: carerId })
        return {
          name: carer?.name
            ? `${carer.name.first || ''} ${carer.name.last || ''}`.trim()
            : '',
          viewedOn: seenByItem.viewedOn || '',
          url: carer?.picture
        }
      })
      .filter((avatar) => avatar.name)

    return <SeenBySection filteredAvatars={filteredAvatars} />
  }

  return (
    <div ref={formRef} style={styles.wrapper}>
      {renderHeader()}
      <SectionsStack>
        {isPBSAssessment && (
          <CarePlanSectionContainer
            sections={(carePlan?.sections || []).filter((s) => !s.sectionType)}
            isViewMode
          />
        )}

        {!isPBSAssessment && (
          <CarePlanFormCard
            title={<FormCardHeading level={3}>Strengths</FormCardHeading>}
          >
            <NotesBlock>
              <NotesLabel weight="medium">Notes</NotesLabel>
              {richTextEnabled &&
              hasMeaningfulStoredHtml(carePlan.strengthsHtml) ? (
                <ViewStoredHtml
                  html={prepareRichEditorContent(carePlan.strengthsHtml)}
                />
              ) : (
                <NotesText>{carePlan.strengths}</NotesText>
              )}
            </NotesBlock>
          </CarePlanFormCard>
        )}

        {!isPBSAssessment && needsCarePlanFlagEnabled && (
          <NeedsSection
            residentId={id}
            residentNeeds={carePlan.residentNeeds || []}
            residentNeedsSnapshot={carePlan.residentNeedsSnapshot || []}
            value={carePlan.residentNeedIds || []}
            isRevision={isViewingRevision}
            disabled
            lastReviewDate={carePlan.editedOn}
            notesValue={carePlan.needs}
            notesHtmlValue={richTextEnabled ? carePlan.needsHtml : undefined}
            legacySkillNeedIds={
              features?.features?.skillsAndNeeds?.enabled
                ? skillNeeds
                : undefined
            }
            legacyNeedOptions={needsOptions}
          />
        )}

        {!isPBSAssessment && !needsCarePlanFlagEnabled && (
          <CarePlanFormCard title="Needs">
            {features?.features?.skillsAndNeeds?.enabled ? (
              <LegacySkillNeeds
                skillNeedIds={skillNeeds}
                needOptions={needsOptions}
              />
            ) : null}
            <CarePlanSectionContent
              value={
                richTextEnabled && hasMeaningfulStoredHtml(carePlan.needsHtml)
                  ? carePlan.needsHtml
                  : carePlan.needs
              }
              richText={
                richTextEnabled && hasMeaningfulStoredHtml(carePlan.needsHtml)
              }
            />
          </CarePlanFormCard>
        )}

        {renderRisks()}

        {hasGoals && (
          <CarePlanGoalsListInput
            disabled
            value={carePlan.goals || []}
            compactSpacing
          />
        )}

        {renderFluidTracking()}

        {(() => {
          const structuredSections = (carePlan?.sections || []).filter(
            (s) => s.sectionType && s.entries?.length > 0
          )
          return structuredSections.length > 0 ? (
            <EndOfLifeSections
              sections={structuredSections}
              onChange={() => {}}
              customSectionFlagEnabled={customCareplanSectionFlagEnabled}
              isEolCarePlan={isEolCarePlan}
              isViewMode
            />
          ) : null
        })()}

        {renderDocuments()}

        {informedConsentFlagEnabled && (
          <ConsentSection
            residentId={id}
            consentForms={carePlan.consentForms}
            consentFormsSnapshot={carePlan.consentFormsSnapshot}
            isRevision={isViewingRevision}
            disabled
          />
        )}

        {renderNextReview()}
        {renderSeenBy()}

        {carePlanSignatures && signatureItem && (
          <Signatures
            carePlan={carePlan}
            logs={logs}
            carers={carers}
            resident={resident}
            setLoadedListItem={setLoadedListItem}
            preferredCarePlanName={preferredCarePlanName}
            item={signatureItem.id}
            category={signatureItem.category}
            informedConsentEnabled={informedConsentFlagEnabled}
          />
        )}
      </SectionsStack>
      {!isViewingRevision && (
        <div
          className="lmc-fixed-footer"
          style={{
            ...styles.footer,
            width: isBrowser || isTablet ? 'calc(100vw - 290px)' : '100vw'
          }}
        >
          <Button
            id="review-care-plan"
            dataTestId="cp-review"
            onClick={onEdit}
            style={styles.updateButton}
            variant="primary"
            disabledByPermission={!hasRolePermission}
          >
            {`Review ${preferredCarePlanName}`}
          </Button>
        </div>
      )}
    </div>
  )
}

const styles = {
  needTextItem: { color: colors.black, marginBottom: 0 },
  wrapper: {
    margin: '0 auto',
    maxWidth: sizes.childScreen,
    paddingTop: 24,
    paddingBottom: 80
  },
  editButton: {
    float: 'right'
  },
  divider: {
    display: 'flex',
    height: 2
  },
  footer: {
    border: 'none',
    display: 'flex',
    justifyContent: 'flex-end'
  },
  subHeading: {
    fontSize: 16,
    opacity: 0.8
  },
  sectionContent: {
    paddingTop: 15,
    minHeight: 100,
    whiteSpace: 'pre-wrap'
  },
  actionsSection: {
    paddingTop: 15,
    minHeight: 100,
    whiteSpace: 'pre-wrap'
  },
  risksSection: {
    paddingTop: 5,
    minHeight: 100
  },
  updateButton: {
    color: 'white',
    textShadow: 'none'
  }
}

const CarePlanFormCard = styled(FormCard)`
  .ant-divider {
    margin: 16px 0 !important;
  }
`

const WrapperActions = styled.div`
  display: flex;
  flex-direction: column;
  padding-top: 16px;
  margin-top: 16px;
  padding-bottom: 16px;
  margin-bottom: 16px;
  padding: 24px;
  background-color: ${(props) => props.theme.color.neutral.color10};
  border-radius: 15px;
`

const StyledHeading = styled(Heading)`
  color: ${(props) => props.theme.color.neutral.color4};
  border-bottom: 1px solid;
  border-color: ${(props) => props.theme.color.neutral.color8};
  margin-bottom: 10px;
  padding-bottom: 10px;
`

const StyledText = styled(Text)`
  color: ${(props) => props.theme.color.neutral.color3};
  white-space: pre-wrap;
`

const mapStateToProps = (state) => ({
  signatureItem: _.find(state.data['log-category-items'], {
    itemId: 'signature'
  }),
  carePlanSignatures: state.global.home.features?.carePlanSignatures?.enabled,
  carers: state.data.carers,
  residents: state.data.residents,
  documents: state.data.documents,
  carePlanNominative: state.global.home.preferredCarePlansNominative,
  needsOptions: state.data['skill-need-templates/needs'],
  features: state.global.home,
  hasGoals: state.global.home.features?.goals?.enabled,
  hasAccess: (roles, abilities) =>
    Selectors.checkRoleAccess(state, { roles, abilities })
})

const mapDispatchToProps = (dispatch) => ({
  setLoadedListItem: (listId, item) =>
    dispatch(Actions.setLoadedListItem(listId, item)),
  fetchNeedsOptions: () =>
    dispatch(Operations.loadList('skill-need-templates/needs')),
  fetchSkillNeedTemplates: () =>
    dispatch(Operations.loadList('skill-need-templates'))
})

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(CarePlanViewScreen)
)
